import React, { FC } from 'react'
import Head from 'next/head'
import SALink from 'components/SALink'
import styles from './Pagination.module.scss'
import PreviousIcon from 'svg/left-caret.svg'
import NextIcon from 'svg/right-caret.svg'
import classNames from 'classnames'

type PaginationProps = {
  current: number
  total: number
  onClickLink: ({ page, currentHref }: { page: number; currentHref: string }) => void
  urlWithParam: ({ page }: { page: number }) => string
  isNextBackVisibleOnMobile?: boolean
}

const initialMaxPagesToShow = 5
const maxPagesToShow = 3
const firstPage = 1

const Pagination: FC<PaginationProps> = ({
  current,
  total,
  onClickLink,
  urlWithParam,
  isNextBackVisibleOnMobile = true,
}) => {
  if (!current || !total || total <= 1) {
    return null
  }

  const prevPage = current > 1 ? current - 1 : null
  const nextPage = current < total ? current + 1 : null
  let pagesStart = current - 1

  if (pagesStart < 1) {
    pagesStart = 1
  }

  if (pagesStart + (maxPagesToShow - 1) >= total) {
    pagesStart = total - maxPagesToShow + 1

    if (pagesStart < 1) {
      pagesStart = 1
    }
  }

  const pageRange = Array.from(
    Array(maxPagesToShow < total ? maxPagesToShow : total),
    (x, index) => index + pagesStart
  )
  const initialPageRange = Array.from(
    Array(initialMaxPagesToShow < total ? initialMaxPagesToShow : total),
    (x, index) => index + 1
  )

  const handleClickChangePage = (page: number) => {
    return (ev: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      ev.preventDefault()
      const currentHref = ev?.currentTarget?.href
      onClickLink({ page, currentHref })
    }
  }

  return (
    <div className={styles.pagination}>
      {/* SEO */}
      <Head>
        {prevPage && (
          <link
            rel='prev'
            href={
              prevPage
                ? urlWithParam({
                    page: prevPage === 1 ? 0 : prevPage,
                  })
                : undefined
            }
          />
        )}
        {nextPage && (
          <link
            rel='next'
            href={
              nextPage
                ? urlWithParam({
                    page: nextPage,
                  })
                : undefined
            }
          />
        )}
      </Head>
      {/* Prev Link */}
      <button
        className={classNames(
          {
            [styles.previousPageNot]: prevPage === null,
            [styles.showBack]: isNextBackVisibleOnMobile,
          },
          styles.previousPage
        )}
        disabled={!prevPage}
      >
        <SALink
          href={
            prevPage
              ? urlWithParam({
                  page: prevPage === 1 ? 0 : prevPage,
                })
              : undefined
          }
          onClick={prevPage ? handleClickChangePage(prevPage) : undefined}
          rel={prevPage ? 'follow' : 'nofollow'}
          data-value={prevPage}
          aria-label='Go to previous page'
          title='Previous'
          disabled={true}
        >
          <PreviousIcon />
          <p className={styles.hideBackText}>Back</p>
        </SALink>
      </button>

      {/* when current selected page is <=4 show pages 1,2,3,4,5 */}
      {current <= 4 &&
        initialPageRange.map((thisPage) => (
          <SALink
            key={thisPage}
            tabIndex={0}
            role='button'
            href={
              thisPage !== current
                ? urlWithParam({
                    page: thisPage === 1 ? 0 : thisPage,
                  })
                : undefined
            }
            onClick={thisPage !== current ? handleClickChangePage(thisPage) : undefined}
            data-value={thisPage}
            className={classNames(
              {
                [styles.currentPageNot]: thisPage !== current,
                [styles.hidePageNumbersOnMobile]: isNextBackVisibleOnMobile,
              },
              styles.currentPage
            )}
            aria-label={`Go to page ${thisPage}`}
            title={`Page ${thisPage}`}
          >
            {thisPage}
          </SALink>
        ))}
      {/* when current selected page is >= show pages 1 ... prev,current, next
         Ex: 1 ... 4,5,6 -> current selected page is 5
         Ex: 1 ... 7,8,9 -> current selected page is 8 */}
      {current >= 5 && (
        <>
          <SALink
            onClick={() => handleClickChangePage(1)}
            href={urlWithParam({
              page: 0,
            })}
            data-value={1}
            className={classNames(
              {
                [styles.currentPageNot]: firstPage !== current,
                [styles.hidePageNumbersOnMobile]: isNextBackVisibleOnMobile,
              },
              styles.currentPage
            )}
            aria-label='Go to page 1'
            title={`Page ${firstPage}`}
          >
            {firstPage}
          </SALink>
          <div className={`${styles.ellipses} defaultTypographySmall`}> ... </div>
          {pageRange.map((thisPage) => (
            <SALink
              key={thisPage}
              tabIndex={0}
              role='button'
              href={
                thisPage !== current
                  ? urlWithParam({
                      page: thisPage === 1 ? 0 : thisPage,
                    })
                  : undefined
              }
              onClick={thisPage !== current ? handleClickChangePage(thisPage) : undefined}
              data-value={thisPage}
              className={classNames(
                {
                  [styles.currentPageNot]: thisPage !== current,
                  [styles.hidePageNumbersOnMobile]: isNextBackVisibleOnMobile,
                },
                styles.currentPage
              )}
              aria-label={`Go to page ${thisPage}`}
              title={`Page ${thisPage}`}
            >
              {thisPage}
            </SALink>
          ))}
        </>
      )}
      {/* Next Link */}
      <button
        className={classNames(
          {
            [styles.nextPageNot]: nextPage === null,
            [styles.showNext]: isNextBackVisibleOnMobile,
          },
          styles.nextPage
        )}
        disabled={!nextPage}
      >
        <SALink
          href={
            nextPage
              ? urlWithParam({
                  page: nextPage,
                })
              : undefined
          }
          onClick={nextPage ? handleClickChangePage(nextPage) : undefined}
          rel={nextPage ? 'follow' : 'nofollow'}
          data-value={nextPage}
          aria-label='Go to next page'
          title='Next'
        >
          <p className={styles.hideNextText}>Next</p>
          <NextIcon />
        </SALink>
      </button>
    </div>
  )
}

export default Pagination
